import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "react-router";
import { useGetList } from "ra-core";
import type { OrganizationMember } from "@/modules/types";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgPresence } from "@/modules/messages/useOrgPresence";
import { parseWorkLocation } from "@/modules/teamMap/teamMapCities";
import { TeamMapMemberCard } from "@/modules/teamMap/TeamMapMemberCard";
import type { TeamMapMember } from "@/modules/teamMap/TeamMapScene";

// three.js loads only when the map opens.
const TeamMapScene = lazy(() => import("@/modules/teamMap/TeamMapScene"));

type MemberWithLocation = OrganizationMember & { work_location?: unknown };

/**
 * Dashboard → Team Map: the team on a 3D map of the Americas, one desk per
 * member at the city set in their profile. Click a desk for their card.
 */
export const TeamMapView = () => {
  const { isOnline } = useOrgPresence();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: members = [], isPending } = useGetList<MemberWithLocation>(
    "organization_members",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "first_name", order: "ASC" },
    },
  );

  const { placed, unplaced } = useMemo(() => {
    const active = members.filter((member) => member.disabled !== true);
    const placed: TeamMapMember[] = [];
    const unplaced: MemberWithLocation[] = [];
    for (const member of active) {
      const location = parseWorkLocation(member.work_location);
      if (location) {
        placed.push({ member, location, online: isOnline(member.id) });
      } else {
        unplaced.push(member);
      }
    }
    return { placed, unplaced };
  }, [members, isOnline]);

  const selected =
    placed.find((entry) => String(entry.member.id) === selectedId) ?? null;

  return (
    <div className="relative h-[calc(100vh-11rem)] min-h-[480px] overflow-hidden rounded-xl border bg-card">
      {isPending ? (
        <Skeleton className="size-full" />
      ) : (
        <Suspense fallback={<Skeleton className="size-full" />}>
          <TeamMapScene
            members={placed}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </Suspense>
      )}

      <div className="pointer-events-none absolute top-3 left-3 flex flex-col gap-1">
        <p className="rounded-md bg-card/90 px-2 py-1 text-xs text-muted-foreground">
          Drag to turn · scroll to zoom · click a desk
        </p>
        {unplaced.length ? (
          <p className="pointer-events-auto rounded-md bg-card/90 px-2 py-1 text-xs text-muted-foreground">
            Not on the map:{" "}
            {unplaced
              .map((member) => member.first_name || member.email)
              .join(", ")}
            .{" "}
            <Link to="/profile" className="text-primary hover:underline">
              Set your city in Profile
            </Link>
          </p>
        ) : null}
      </div>

      {selected ? (
        <div className="absolute top-3 right-3 bottom-3 flex">
          <TeamMapMemberCard
            entry={selected}
            onClose={() => setSelectedId(null)}
          />
        </div>
      ) : null}
    </div>
  );
};
